import {
  Avatar,
  Button,
  Descriptions,
  Drawer,
  Dropdown,
  Empty,
  Input,
  Modal,
  Popconfirm,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from "antd"
import {
  BankOutlined,
  DeleteOutlined,
  EditOutlined,
  EllipsisOutlined,
  EyeOutlined,
  LinkOutlined,
  PlusOutlined,
  SendOutlined,
} from "@ant-design/icons"
import { useState, useEffect } from "react"
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore"
import { db } from "../../firebase/config"
import { useAuthContext } from "../../hooks/useAuthContext"
import moment from "moment"
import CreateClientPopup from "./CreateClientPopup"
import EditClientFullDrawer from "./EditClientFullDrawer"
import ShowSavedPricesDrawer from "../Manager/Prices/ShowSavedPricesDrawer"
import { CountryCodesSelect } from "./CountryCodesSelect"
import { OrderLocation } from "../Orders/OrderLocation"
import { returnProperMachineTag } from "../utils/ReturnProperMachineTag"
import OrderDetailDrawer from "./OrderDetailDrawer"

const { Text, Link } = Typography

// --- Helpers ---


const ROLE_COLORS = { Admin: "red", Manager: "blue", "Dev Spec": "purple", Sales: "green" }

function deriveRole({ admin, semiAdmin, devSpecialist, sales }) {
  if (admin) return "Admin"
  if (semiAdmin) return "Manager"
  if (devSpecialist) return "Dev Spec"
  if (sales) return "Sales"
  return "Sales"
}

function getDisplayFields(record, type) {
  if (type === "lead") {
    return {
      email: record.email,
      country: record.country,
      phone: record.phone,
      contactPerson: record.contactPerson,
      address: "",
      notes: record.comments,
      createdSeconds: record.createdAt?.seconds,
    }
  }
  return {
    email: record.contactEmail,
    country: record.country,
    phone: record.contactNumber,
    contactPerson: record.contactName,
    address: [record.street, record.city, record.postalCode].filter(Boolean).join(", "),
    notes: "",
    createdSeconds: record.date?.createdAt?.seconds,
  }
}

// --- Small shared components ---


function SectionHeader({ title, action }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: 12,
      }}>
      <Text
        strong
        style={{ fontSize: 11, letterSpacing: 0.8, textTransform: "uppercase", color: "#888" }}>
        {title}
      </Text>
      {action}
    </div>
  )
}

function CommentCard({ c, onDelete, onEdit }) {
  const { user, admin } = useAuthContext()
  const [editing, setEditing] = useState(false)
  const [editText, setEditText] = useState(c.text)
  const [saving, setSaving] = useState(false)

  const canModify = c.authorId === user?.uid || admin
  const initials = (c.authorName || c.authorEmail || "?")[0].toUpperCase()

  const handleSave = async () => {
    if (!editText.trim()) return
    setSaving(true)
    await onEdit(c.id, editText.trim())
    setSaving(false)
    setEditing(false)
  }

  const menuItems = [
    {
      key: "edit",
      label: "Edit",
      icon: <EditOutlined />,
      onClick: () => { setEditText(c.text); setEditing(true) },
    },
    {
      key: "delete",
      label: "Delete",
      icon: <DeleteOutlined />,
      danger: true,
      onClick: () =>
        Modal.confirm({
          title: "Delete this comment?",
          okText: "Delete",
          okButtonProps: { danger: true },
          onOk: () => onDelete(c.id),
        }),
    },
  ]

  return (
    <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
      <Avatar
        size={32}
        style={{ backgroundColor: "#1677ff", fontSize: 13, flexShrink: 0, lineHeight: "32px" }}>
        {initials}
      </Avatar>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 3 }}>
          <Space size={6}>
            <Text strong style={{ fontSize: 13 }}>{c.authorName || c.authorEmail}</Text>
            {c.authorRole && (
              <Tag
                color={ROLE_COLORS[c.authorRole] || "default"}
                style={{ fontSize: 10, padding: "0 5px", margin: 0, lineHeight: "18px" }}>
                {c.authorRole}
              </Tag>
            )}
          </Space>
          {canModify && (
            <Dropdown menu={{ items: menuItems }} trigger={["click"]} placement="bottomRight">
              <Button type="text" size="small" icon={<EllipsisOutlined />} style={{ flexShrink: 0 }} />
            </Dropdown>
          )}
        </div>

        {editing ? (
          <Space direction="vertical" style={{ width: "100%", marginBottom: 4 }}>
            <Input.TextArea
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              autoSize={{ minRows: 2 }}
            />
            <Space size="small">
              <Button size="small" type="primary" loading={saving} onClick={handleSave}>Save</Button>
              <Button size="small" onClick={() => setEditing(false)}>Cancel</Button>
            </Space>
          </Space>
        ) : (
          <Text style={{ fontSize: 13, display: "block" }}>{c.text}</Text>
        )}

        <Text type="secondary" style={{ fontSize: 11 }}>
          {c.createdAt?.seconds ? moment.unix(c.createdAt.seconds).format("DD/MM/YYYY HH:mm") : ""}
        </Text>
      </div>
    </div>
  )
}

function CommentInput({ onPost, posting }) {
  const [value, setValue] = useState("")

  const submit = async () => {
    if (!value.trim() || posting) return
    await onPost(value.trim())
    setValue("")
  }

  return (
    <div
      style={{
        display: "flex",
        gap: 8,
        alignItems: "flex-end",
        borderTop: "1px solid #f0f0f0",
        paddingTop: 12,
        marginTop: 4,
      }}>
      <Input.TextArea
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="Add a comment..."
        autoSize={{ minRows: 1, maxRows: 4 }}
        style={{ flex: 1 }}
        onPressEnter={(e) => {
          if (!e.shiftKey) {
            e.preventDefault()
            submit()
          }
        }}
      />
      <Button type="primary" icon={<SendOutlined />} onClick={submit} loading={posting}>
        Post
      </Button>
    </div>
  )
}

// --- Tab: Overview ---

function FormField({ label, value, onChange, textarea }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <Text type="secondary" style={{ fontSize: 11, display: "block", marginBottom: 2 }}>
        {label}
      </Text>
      {textarea ? (
        <Input.TextArea value={value} onChange={onChange} autoSize={{ minRows: 2 }} />
      ) : (
        <Input value={value} onChange={onChange} />
      )}
    </div>
  )
}

function OverviewTab({ record, type, fields, onDelete, onClose, comments, onPost, posting, allPrices, ptLoading, onDeleteComment, onEditComment }) {
  const isLead = type === "lead"
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState({})
  const [saving, setSaving] = useState(false)
  const [editOpen, setEditOpen] = useState(false)

  const setField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }))

  const startEdit = () => {
    setForm({
      client: record.client || "",
      vat: record.vat || "",
      reg: record.reg || "",
      email: fields.email || "",
      country: fields.country || "",
      phone: fields.phone || "",
      contactPerson: fields.contactPerson || "",
      notes: fields.notes || "",
    })
    setEditing(true)
  }

  const cancelEdit = () => setEditing(false)

  const handleSave = async () => {
    setSaving(true)
    try {
      const colName = isLead ? "leads" : "clients"
      const updateData = isLead
        ? {
            client: form.client,
            vat: form.vat,
            reg: form.reg,
            email: form.email,
            country: form.country,
            phone: form.phone,
            contactPerson: form.contactPerson,
            comments: form.notes,
          }
        : {
            client: form.client,
            vat: form.vat,
            reg: form.reg,
            contactEmail: form.email,
            country: form.country,
            contactNumber: form.phone,
            contactName: form.contactPerson,
          }
      await updateDoc(doc(db, colName, record.id), updateData)
      message.success("Saved successfully!")
      setEditing(false)
    } catch (err) {
      console.error(err)
      message.error("Failed to save changes")
    } finally {
      setSaving(false)
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>

      {/* ── HEADER ── */}
      <div style={{
        display: "flex", gap: 16, alignItems: "flex-start",
        paddingBottom: 20, marginBottom: 20, borderBottom: "1px solid #f0f0f0",
      }}>
        <Avatar
          size={64}
          icon={<BankOutlined />}
          src={record.clientImage || undefined}
          style={{
            backgroundColor: "#eff6ff", color: "#2563eb", flexShrink: 0,
            fontSize: 28, border: "2px solid #dbeafe",
          }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
            <Text strong style={{ fontSize: 16 }}>{record.client}</Text>
            <Tag color={isLead ? "blue" : "green"} style={{ margin: 0, fontWeight: 500 }}>
              {isLead ? "Lead" : "Client"}
            </Tag>
          </div>
          <Space direction="vertical" size={2}>
            {record.vat && <Text type="secondary" style={{ fontSize: 12 }}>VAT · {record.vat}</Text>}
            {record.reg && <Text type="secondary" style={{ fontSize: 12 }}>Reg · {record.reg}</Text>}
            {fields.country && <Text type="secondary" style={{ fontSize: 12 }}>Country · {fields.country}</Text>}
            {fields.createdSeconds && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Created · {moment.unix(fields.createdSeconds).format("DD/MM/YYYY HH:mm")}
              </Text>
            )}
          </Space>
        </div>
        <Space direction="vertical" size={8} style={{ flexShrink: 0 }}>
          {isLead ? (
            <CreateClientPopup prefill={record} icon={false} />
          ) : (
            <>
              <Button icon={<EditOutlined />} size="small" type="primary" onClick={() => setEditOpen(true)}>
                Edit Client
              </Button>
              <EditClientFullDrawer record={record} open={editOpen} onClose={() => setEditOpen(false)} />
            </>
          )}
          <Popconfirm
            title={`Delete this ${isLead ? "lead" : "client"}?`}
            onConfirm={() => { onDelete(record.id); onClose() }}>
            <Button danger size="small" icon={<DeleteOutlined />} block>Delete</Button>
          </Popconfirm>
        </Space>
      </div>

      {/* ── CLIENT INFORMATION ── */}
      <div style={{ marginBottom: 24 }}>
        <SectionHeader
          title="Client Information"
          action={
            editing ? (
              <Space size={4}>
                <Button size="small" onClick={cancelEdit}>Cancel</Button>
                <Button size="small" type="primary" loading={saving} onClick={handleSave}>Save</Button>
              </Space>
            ) : (
              <Link style={{ fontSize: 12 }} onClick={startEdit}>
                <EditOutlined /> Edit
              </Link>
            )
          }
        />
        {editing ? (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
            <FormField label="Company Name" value={form.client} onChange={setField("client")} />
            <FormField label="VAT Number" value={form.vat} onChange={setField("vat")} />
            <FormField label="Registration Number" value={form.reg} onChange={setField("reg")} />
            <FormField label="Email" value={form.email} onChange={setField("email")} />
            <div style={{ marginBottom: 8 }}>
              <Text type="secondary" style={{ fontSize: 11, display: "block", marginBottom: 2 }}>Country</Text>
              <CountryCodesSelect
                defaultCountry={form.country}
                setSelectedCountry={(v) => setForm((f) => ({ ...f, country: v }))}
                setSelectedCountryCode={() => {}}
              />
            </div>
            <FormField label="Phone" value={form.phone} onChange={setField("phone")} />
            <FormField label="Contact Person" value={form.contactPerson} onChange={setField("contactPerson")} />
            {isLead && (
              <div style={{ gridColumn: "1 / -1" }}>
                <FormField label="Notes" value={form.notes} onChange={setField("notes")} textarea />
              </div>
            )}
          </div>
        ) : (
          <Descriptions
            bordered
            size="small"
            column={2}
            items={[
              { key: "client", label: "Company Name", children: record.client || "—" },
              { key: "vat", label: "VAT Number", children: record.vat || "—" },
              { key: "reg", label: "Reg Number", children: record.reg || "—" },
              { key: "email", label: "Email", children: fields.email || "—" },
              { key: "country", label: "Country", children: fields.country || "—" },
              { key: "phone", label: "Phone", children: fields.phone || "—" },
              ...(fields.address ? [{ key: "address", label: "Address", span: 2, children: fields.address }] : []),
              ...(fields.contactPerson ? [{ key: "contact", label: "Contact Person", span: 2, children: fields.contactPerson }] : []),
              ...(fields.notes ? [{ key: "notes", label: "Notes", span: 2, children: fields.notes }] : []),
            ]}
          />
        )}
      </div>

      {/* ── RECENT PRICES ── */}
      <div style={{ marginBottom: 24 }}>
        <SectionHeader title="Recent Prices" />
        <Table
          size="small"
          loading={ptLoading}
          dataSource={allPrices.slice(0, 3)}
          rowKey={(r) => r.id}
          pagination={false}
          columns={PRICE_COLUMNS_COMPACT}
          locale={{ emptyText: <Empty description="No prices yet" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
          style={{ marginBottom: allPrices.length > 3 ? 6 : 0 }}
        />
        {allPrices.length > 3 && (
          <Text style={{ fontSize: 12, color: "#1677ff" }}>
            +{allPrices.length - 3} more — see Prices tab
          </Text>
        )}
      </div>

      {/* ── COMMENTS ── */}
      <div>
        <SectionHeader
          title="Comments"
          action={
            <Button type="link" size="small" icon={<PlusOutlined />}
              style={{ padding: 0, fontSize: 12, height: "auto" }}>
              Add Comment
            </Button>
          }
        />
        {comments.length === 0 ? (
          <Text type="secondary" style={{ fontSize: 12 }}>No comments yet.</Text>
        ) : (
          comments.slice(-2).map((c) => (
            <CommentCard key={c.id} c={c} onDelete={onDeleteComment} onEdit={onEditComment} />
          ))
        )}
        <CommentInput onPost={onPost} posting={posting} />
      </div>

    </div>
  )
}

// --- Price helpers ---

const MACHINE_COLORS = { Arsoma: "purple", Viva: "blue", Tape: "orange", Winder: "cyan" }
const MACHINE_FILTERS = Object.keys(MACHINE_COLORS).map((m) => ({ text: m, value: m }))

function priceDisplay(p) {
  if (p._type === "tape") {
    const first = p.prices?.[0]
    return first ? `${first.rolls} rolls — €${first.price}` : "—"
  }
  const first = p.dynamicPrice?.[0]
  return first ? `${first.quantity} pcs — €${first.price}` : "—"
}

function getNumericPrice(p) {
  if (p._type === "tape") return p.prices?.[0]?.price || 0
  return p.dynamicPrice?.[0]?.price || 0
}

function lastEditDate(p) {
  return p.sortDate ? moment.unix(p.sortDate).format("DD/MM/YY HH:mm") : "—"
}

function TechLink({ url }) {
  if (!url) return <Text type="secondary" style={{ fontSize: 11 }}>—</Text>
  return (
    <a href={url} target="_blank" rel="noreferrer">
      <LinkOutlined style={{ fontSize: 13 }} />
    </a>
  )
}

const S = { fontSize: 11 }

// Compact columns for the Overview preview (no filters/sorters)
const PRICE_COLUMNS_COMPACT = [
  {
    title: "Product",
    dataIndex: ["product", "productName"],
    width: 140,
    ellipsis: true,
    render: (v) => <Text style={S}>{v || "—"}</Text>,
  },
  {
    title: "Machine",
    dataIndex: "machine",
    width: 70,
    align: "center",
    render: (v) => (
      <Tag color={MACHINE_COLORS[v] || "default"} style={{ fontSize: 10, margin: 0, padding: "0 4px" }}>
        {v || "—"}
      </Tag>
    ),
  },
  {
    title: "Price",
    width: 130,
    render: (_, r) => <Text style={S}>{priceDisplay(r)}</Text>,
  },
  {
    title: "Tech",
    width: 36,
    align: "center",
    render: (_, r) => <TechLink url={r.technicalPage} />,
  },
  {
    title: "Last Edit",
    width: 90,
    render: (_, r) => <Text style={S}>{lastEditDate(r)}</Text>,
  },
  {
    title: "",
    width: 36,
    align: "center",
    render: (_, r) =>
      r._type === "label" ? (
        <ShowSavedPricesDrawer
          data={r}
          triggerElement={<Button type="text" size="small" icon={<EyeOutlined style={{ color: "#1677ff" }} />} />}
        />
      ) : null,
  },
]

// Full columns for the Prices tab (with filters and sorters)
const PRICE_COLUMNS_FULL = [
  {
    title: "Product",
    dataIndex: ["product", "productName"],
    ellipsis: true,
    sorter: (a, b) => (a.product?.productName || "").localeCompare(b.product?.productName || ""),
    render: (v) => <Text style={{ fontSize: 12 }}>{v || "—"}</Text>,
  },
  {
    title: "Machine",
    dataIndex: "machine",
    width: 90,
    filters: MACHINE_FILTERS,
    onFilter: (value, record) => record.machine === value,
    render: (v) => (
      <Tag color={MACHINE_COLORS[v] || "default"} style={{ fontSize: 11, margin: 0 }}>
        {v || "—"}
      </Tag>
    ),
  },
  {
    title: "Starting Price",
    width: 150,
    sorter: (a, b) => getNumericPrice(a) - getNumericPrice(b),
    render: (_, r) => <Text style={{ fontSize: 12 }}>{priceDisplay(r)}</Text>,
  },
  {
    title: "Tech.",
    width: 70,
    align: "center",
    render: (_, r) => <TechLink url={r.technicalPage} />,
  },
  {
    title: "Last Edit",
    width: 120,
    defaultSortOrder: "descend",
    sorter: (a, b) => a.sortDate - b.sortDate,
    render: (_, r) => <Text style={{ fontSize: 12 }}>{lastEditDate(r)}</Text>,
  },
  {
    title: "",
    width: 40,
    align: "center",
    render: (_, r) =>
      r._type === "label" ? (
        <ShowSavedPricesDrawer
          data={r}
          triggerElement={<Button type="text" size="small" icon={<EyeOutlined style={{ color: "#1677ff" }} />} />}
        />
      ) : null,
  },
]

// --- Tab: Prices (full) ---

function PricesTab({ allPrices, loading }) {
  return (
    <Table
      size="small"
      loading={loading}
      dataSource={allPrices}
      rowKey={(r) => r.id}
      pagination={{ defaultPageSize: 10, showSizeChanger: true }}
      columns={PRICE_COLUMNS_FULL}
      locale={{
        emptyText: <Empty description="No prices found" image={Empty.PRESENTED_IMAGE_SIMPLE} />,
      }}
    />
  )
}

// --- Tab: Comments (full) ---

function CommentsTab({ comments, onPost, posting, onDeleteComment, onEditComment }) {
  return (
    <Space direction="vertical" style={{ width: "100%" }}>
      <div style={{ maxHeight: "calc(100vh - 340px)", overflowY: "auto", paddingRight: 4 }}>
        {comments.length === 0 ? (
          <Empty description="No comments yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          comments.map((c) => (
            <CommentCard key={c.id} c={c} onDelete={onDeleteComment} onEdit={onEditComment} />
          ))
        )}
      </div>
      <CommentInput onPost={onPost} posting={posting} />
    </Space>
  )
}

// --- Tab: Activity (Order History) ---

function ActivityTab({ orders, loading }) {
  const [selectedOrder, setSelectedOrder] = useState(null)

  const statusFilters = [...new Map((orders || []).map((o) => [o.orderStatus, o])).values()].map(
    (o) => ({ text: OrderLocation(o.orderStatus) || o.orderStatus, value: o.orderStatus })
  )

  const columns = [
    {
      title: "Order #",
      dataIndex: "orderNumber",
      width: 85,
      sorter: (a, b) => (a.justOrderNumber || 0) - (b.justOrderNumber || 0),
      render: (v, record) => (
        <Link onClick={() => setSelectedOrder(record)} style={{ fontSize: 12 }}>{v || "—"}</Link>
      ),
    },
    {
      title: "Product",
      dataIndex: ["selectedProduct", "product", "productName"],
      ellipsis: true,
      render: (v) => <Text style={{ fontSize: 12 }}>{v || "—"}</Text>,
    },
    {
      title: "Machine",
      dataIndex: ["selectedProduct", "machine"],
      width: 80,
      filters: [
        { text: "Arsoma", value: "Arsoma" },
        { text: "Viva", value: "Viva" },
        { text: "Tape", value: "Tape" },
        { text: "Cutter", value: "Cutter" },
      ],
      onFilter: (value, record) => record.selectedProduct?.machine === value,
      render: (v) => returnProperMachineTag(v),
    },
    {
      title: "Qty",
      width: 75,
      align: "right",
      sorter: (a, b) => (a.labelCount || 0) - (b.labelCount || 0),
      render: (_, r) => (
        <Text style={{ fontSize: 12 }}>
          {(r.selectedProduct?.machine === "Tape" ? r.rollCount : r.labelCount)?.toLocaleString() || "—"}
        </Text>
      ),
    },
    {
      title: "Price",
      dataIndex: "price",
      width: 60,
      align: "right",
      sorter: (a, b) => (a.price || 0) - (b.price || 0),
      render: (v) => <Text style={{ fontSize: 12 }}>{v != null ? `€${v}` : "—"}</Text>,
    },
    {
      title: "Status",
      dataIndex: "orderStatus",
      width: 95,
      filters: statusFilters,
      onFilter: (value, record) => record.orderStatus === value,
      render: (v) => {
        const color = v === 7 ? "green" : v === 6 ? "blue" : v >= 3 ? "orange" : "default"
        return <Tag color={color} style={{ fontSize: 11, margin: 0 }}>{OrderLocation(v) || v}</Tag>
      },
    },
    {
      title: "Due",
      dataIndex: "orderDueDate",
      width: 78,
      sorter: (a, b) => (a.orderDueDate?.seconds || 0) - (b.orderDueDate?.seconds || 0),
      render: (v) => (
        <Text style={{ fontSize: 11 }}>{v?.seconds ? moment.unix(v.seconds).format("DD/MM/YY") : "—"}</Text>
      ),
    },
    {
      title: "Created",
      dataIndex: "created",
      width: 78,
      defaultSortOrder: "descend",
      sorter: (a, b) => (a.created?.seconds || 0) - (b.created?.seconds || 0),
      render: (v) => (
        <Text style={{ fontSize: 11 }}>{v?.seconds ? moment.unix(v.seconds).format("DD/MM/YY") : "—"}</Text>
      ),
    },
  ]

  return (
    <>
      <Table
        size="small"
        loading={loading}
        dataSource={orders}
        rowKey={(r) => r.id}
        columns={columns}
        pagination={{ defaultPageSize: 10, showSizeChanger: true }}
        locale={{ emptyText: <Empty description="No orders found" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
        scroll={{ x: 680 }}
      />
      <OrderDetailDrawer
        order={selectedOrder}
        open={!!selectedOrder}
        onClose={() => setSelectedOrder(null)}
      />
    </>
  )
}

// --- Main drawer ---

export default function ClientDetailDrawer({ record, type = "lead", open, onClose, onDelete }) {
  const { user, admin, semiAdmin, devSpecialist } = useAuthContext()
  const [liveRecord, setLiveRecord] = useState(record)
  const [comments, setComments] = useState([])
  const [posting, setPosting] = useState(false)
  const [allPrices, setAllPrices] = useState([])
  const [ptLoading, setPtLoading] = useState(false)
  const [clientOrders, setClientOrders] = useState([])
  const [ordersLoading, setOrdersLoading] = useState(false)

  const collectionBase = type === "lead" ? "leads" : "clients"

  // Keep liveRecord in sync with the prop when a different record is opened
  useEffect(() => {
    setLiveRecord(record)
  }, [record])

  // Subscribe to the specific document so edits reflect immediately
  useEffect(() => {
    if (!record?.id || !open) return
    const unsub = onSnapshot(doc(db, collectionBase, record.id), (snap) => {
      if (snap.exists()) setLiveRecord({ id: snap.id, ...snap.data() })
    })
    return () => unsub()
  }, [record?.id, open, collectionBase])

  // Comments subscription
  useEffect(() => {
    if (!record?.id || !open) return
    const q = query(
      collection(db, collectionBase, record.id, "comments"),
      orderBy("createdAt", "asc")
    )
    const unsub = onSnapshot(q, (snap) => {
      setComments(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    })
    return () => unsub()
  }, [record?.id, open, collectionBase])

  // Fetch prices from both collections on open (one-time, not a listener)
  useEffect(() => {
    if (!record?.id || !open) return
    let cancelled = false
    setPtLoading(true)

    Promise.all([
      getDocs(query(collection(db, "priceCalculation"), where("clientId", "==", record.id))),
      getDocs(query(collection(db, "tapePrices"), where("clientId", "==", record.id))),
    ])
      .then(([pcSnap, tpSnap]) => {
        if (cancelled) return
        const pc = pcSnap.docs.map((d) => ({
          id: d.id,
          _type: "label",
          sortDate: d.data().date?.lastEdit?.seconds || d.data().date?.created?.seconds || 0,
          ...d.data(),
        }))
        const tp = tpSnap.docs.map((d) => ({
          id: d.id,
          _type: "tape",
          sortDate: d.data().updated?.seconds || d.data().created?.seconds || 0,
          ...d.data(),
        }))
        const combined = [...pc, ...tp].sort((a, b) => b.sortDate - a.sortDate)
        setAllPrices(combined)
      })
      .catch((err) => { if (!cancelled) console.error(err) })
      .finally(() => { if (!cancelled) setPtLoading(false) })

    return () => { cancelled = true }
  }, [record?.id, open])

  // Fetch order history on open
  useEffect(() => {
    if (!record?.id || !open) return
    let cancelled = false
    setOrdersLoading(true)
    getDocs(query(collection(db, "orders"), where("clientId", "==", record.id)))
      .then((snap) => {
        if (cancelled) return
        const orders = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (b.created?.seconds || 0) - (a.created?.seconds || 0))
        setClientOrders(orders)
      })
      .catch((err) => { if (!cancelled) console.error(err) })
      .finally(() => { if (!cancelled) setOrdersLoading(false) })
    return () => { cancelled = true }
  }, [record?.id, open])

  const handleDeleteComment = async (commentId) => {
    try {
      await deleteDoc(doc(db, collectionBase, record.id, "comments", commentId))
    } catch {
      message.error("Failed to delete comment")
    }
  }

  const handleEditComment = async (commentId, newText) => {
    try {
      await updateDoc(doc(db, collectionBase, record.id, "comments", commentId), { text: newText })
    } catch {
      message.error("Failed to update comment")
    }
  }

  const handlePost = async (text) => {
    setPosting(true)
    try {
      await addDoc(collection(db, collectionBase, record.id, "comments"), {
        text,
        authorId: user.uid,
        authorEmail: user.email,
        authorName: user.displayName || user.email,
        authorRole: deriveRole({ admin, semiAdmin, devSpecialist }),
        createdAt: serverTimestamp(),
      })
    } catch {
      message.error("Failed to post comment")
    } finally {
      setPosting(false)
    }
  }

  if (!liveRecord) return null

  const fields = getDisplayFields(liveRecord, type)
  const isLead = type === "lead"

  const tabs = [
    {
      key: "overview",
      label: "Overview",
      children: (
        <OverviewTab
          record={liveRecord}
          type={type}
          fields={fields}
          onDelete={onDelete}
          onClose={onClose}
          comments={comments}
          onPost={handlePost}
          posting={posting}
          allPrices={allPrices}
          ptLoading={ptLoading}
          onDeleteComment={handleDeleteComment}
          onEditComment={handleEditComment}
        />
      ),
    },
    {
      key: "prices",
      label: `Prices (${allPrices.length})`,
      children: <PricesTab allPrices={allPrices} loading={ptLoading} />,
    },
    {
      key: "comments",
      label: `Comments (${comments.length})`,
      children: <CommentsTab comments={comments} onPost={handlePost} posting={posting} onDeleteComment={handleDeleteComment} onEditComment={handleEditComment} />,
    },
    {
      key: "activity",
      label: `Activity (${clientOrders.length})`,
      children: <ActivityTab orders={clientOrders} loading={ordersLoading} />,
    },
  ]

  return (
    <Drawer
      title={`${isLead ? "Lead" : "Client"} – ${record.client || ""}`}
      width={740}
      placement="right"
      open={open}
      onClose={onClose}
      destroyOnClose>
      <Tabs items={tabs} />
    </Drawer>
  )
}
